import { Helmet } from 'react-helmet-async';
import AssetManagementView from './components/AssetView';
import {
  PageTitleWrapper,
  PageHeader,
  SimpleDialog,
  CustomForm,
  ButtonToggle,
  showErrorMessage,
  showSuccessMessage,
  FormField
} from '../../shared/components/index';
import {
  Grid,
  Container,
  Box,
  Typography,
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { useEffect, useState, MouseEvent } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as assetService from '../Asset/services/assetManagement.service';
import { selectUserState } from 'src/auth/login/services/userSlice';
import AddTwoToneIcon from '@mui/icons-material/AddTwoTone';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { FormFieldType } from '../../shared/components/form-field/service/formFieldInterface';
import {
  I18N,
  LIST_VIEW,
  REGEX,
  ROLES,
  TENANT_ROLES
} from '../../shared/constants/constants';
import * as ROUTES from '../../shared/constants/routes';
import GridViewSharpIcon from '@mui/icons-material/GridViewSharp';
import TableRowsTwoToneIcon from '@mui/icons-material/TableRowsTwoTone';
import { VIEW_TYPE } from './utils/utils';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';
import AddAssetModal from './components/AssetModal';
import * as assetTypeService from 'src/modules/Asset-type/services/asset-type_Management.service';
import AssetDetails from './AssetDetails';
import CustomSellModal from './models/modals/CustomSellModal';
import { Asset } from './models';

const AssetManagement = () => {
  const [selectedTab, setSelectedTab] = useState<string | null>(
    LIST_VIEW.TABLE_VIEW
  );
  const [open, setOpen] = useState<boolean>(false);
  const [viewType, setViewType] = useState<string>(VIEW_TYPE.ASSET_MANAGEMENT);
  const [isAddModal, setIsAddModal] = useState<boolean>(true);
  const { t } = useTranslation(I18N.DEFAULT);
  const dispatch = useDispatch<any>();
  const [assetList, setAssetList] = useState([]);
  const {
    tenantAssetList,
    isAssetListUpdated,
    isError,
    errorMessage,
    bookingAssetsData,
    expenseAssetsData,
    isAssetUpdated
  } = useSelector(assetService.selectorAssetState);
  const {
    assetTypeData
  } = useSelector(assetTypeService.selectorAssetTypeState);
  const { selectedOrganization, loginUserRole } = useSelector(selectUserState);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [breadcrumbsHeading, setBreadcrumbsHeading] = useState(t('booking-d-lite'));
  const [headerSubtitle, setHeaderSubtitle] = useState(t('booking-d-lite'));
  const [userAssetsList, setUserAssetsList] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState();
  const [isDetailAssetView, setDetailAIsAssetView] = useState(false);
  const [assetTypeList, setAssetTypeList] = useState([]);
  const [bookingAssetList, setBookingAssetList] = useState([]);
  const [assetExpenceList, setAssetExpenceList] = useState([]);

  const location = useLocation();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(assetService.getAssetByTenantIdData({ tenantId: selectedOrganization.id, isExpenses: true }));
    dispatch(assetTypeService.getAssetTypeData(selectedOrganization.id));
  }, []);

  useEffect(() => {
    setAssetTypeList(assetTypeData);
  }, [assetTypeData]);

  useEffect(() => {
    setBookingAssetList(bookingAssetsData);
  }, [bookingAssetsData]);

  useEffect(() => {
    setAssetExpenceList(expenseAssetsData);
  }, [expenseAssetsData]);

  function setInitialHeaderDetail(
    viewType,
    breadcrumbsHeading,
    subtitle,
    breadcrumbs
  ) {
    setViewType(viewType);
    setBreadcrumbsHeading(breadcrumbsHeading);
    setBreadcrumbs(breadcrumbs);
    setHeaderSubtitle(subtitle);
  }

  useEffect(() => {
    setInitialHeaderDetail(
      VIEW_TYPE.ASSET_MANAGEMENT,
      t('car.asset-management'),
      `${t('car.list-all-user-label')} ${selectedOrganization.tenantName}`,
      getBreadcrumbs(VIEW_TYPE.ASSET_MANAGEMENT)
    );
  }, [location]);

  const getBreadcrumbs = (viewType) => {
    const breadcrumbs = [
      {
        name: t('organization'),
        route: `${ROUTES.ADMIN_LOGIN}${ROUTES.ORGANIZATION}`
      },
      {
        name: selectedOrganization.tenantName,
        route: ROUTES.USER_MANAGEMENT
      }
    ];
    return viewType == VIEW_TYPE.ASSET_MANAGEMENT
      ? breadcrumbs
      : [
        ...breadcrumbs,
        {
          name: params.get('user')
        }
      ];
  };

  useEffect(() => {
    if (isError) {
      dispatch(assetService.setIsError(false));
      if (errorMessage !== '') {
        return showErrorMessage(errorMessage, {
          theme: 'colored',
          position: 'top-right',
          autoClose: 5000
        });
      }
    }
  }, [isError]);

  useEffect(() => {
    dispatch(assetService.getAssetByTenantIdData({ tenantId: selectedOrganization.id, isExpenses: true }));
    const breadcrumbsList = [
      {
        name: `organization`,
        route: `${ROUTES.ADMIN_LOGIN}${ROUTES.ORGANIZATION}`
      },
      {
        name: `${selectedOrganization.tenantName}`
      }
    ];
    setBreadcrumbs(breadcrumbsList);
  }, []);

  useEffect(() => {
    setAssetList(tenantAssetList);
  }, [tenantAssetList]);

  useEffect(() => {
    setOpen(false);
    closeDialog();
    if (isAssetListUpdated) {
      dispatch(assetService.getAssetByTenantIdData({ tenantId: selectedOrganization.id, isExpenses: true }));
      dispatch(assetService.setIsAssetListUpdated(false));
    }
  }, [isAssetListUpdated]);

  useEffect(() => {
    if (isAssetUpdated) {
      dispatch(assetService.getAssetByTenantIdData({ tenantId: selectedOrganization.id, isExpenses: true }));
      dispatch(assetService.setIsAssetUpdated(false));
      return showSuccessMessage(t('car.sold-success'), {
        theme: 'colored',
        position: 'top-right',
        autoClose: 5000
      })
    }
  }, [isAssetUpdated]);

  const handleAddUserButtonClick = () => {
    setIsAddModal(true);
    setOpen(true);
  };

  const handleFilterUsers = (value: string) => {
    const filterUsersData = tenantAssetList.filter(function (item) {
      return item.code.toLowerCase().includes(value.toLowerCase()) || item.name.toLowerCase().includes(value.toLowerCase());
    });
    setAssetList(filterUsersData);
  };

  const handleDeleteIconClick = (row: any) => {
    setOpen(false);
    const payload = {
      id: row.id,
      tenantId: selectedOrganization.id,
    };
    dispatch(assetService.deleteAsset(payload));
  };

  const [isSellModalOpen, setIsSellModalOpen] = useState(false);

  const handleSellIconClick = (row) => {
    setIsSellModalOpen(true);
    setSelectedAsset(row);
  };

  const handleSellModalCancelClick = () => {
    setIsSellModalOpen(false);
    setSelectedAsset(null);
  };

  const handleSellAssetReq = (row: any) => {
    handleSellModalCancelClick();
    const payload = {
      id: row.id,
      tenantId: selectedOrganization.id,
      amountCollections: [
        {
          amount: row.amount,
          paymentMethod: row.paymentMethod
        }
      ]
    };
    dispatch(assetService.sellAsset(payload));
  };

  const handleClose = (value) => {
    setOpen(false);
  };
  const closeDialog = () => {
    setOpen(false);
  };

  const onCancel = () => {
    closeDialog();
  };

  const handleEditIconClick = (row) => {
    setSelectedAsset(row);
    setIsAddModal(false);
    setOpen(true);
  };

  const handleViewIconClick = (row) => {
    setSelectedAsset(row);
    setDetailAIsAssetView(true);
    dispatch(assetService.getFutureBookingByAssetId(row.id));
    if (selectedOrganization?.isAssetSold) {
      dispatch(assetService.getExpensesByAssetId(row.id));
    }
  };

  const handlAssetViewBackClick = (row) => {
    setSelectedAsset(null);
    setDetailAIsAssetView(false);
  };

  const handleSwitchIconClick = (row) => {
    dispatch(
      assetService.updateTenantUserStatus({
        email: row.email,
        tenantId: selectedOrganization.id,
        value: !row.isEnabled
      })
    );
  };

  const onSubmit = (values: any) => {

    const payload: any = values;
    payload.tenantId = selectedOrganization.id;
    setOpen(false);
    if (!isAddModal) {
      dispatch(
        assetService.updateAsset(payload)
      );
    } else {
      dispatch(
        assetService.addAsset(payload)
      );
    }
  };

  const handleViewOrientation = (
    _event: MouseEvent<HTMLElement>,
    newValue: string | null
  ) => {
    if (newValue !== null) {
      setSelectedTab(newValue);
    }
  };

  return (
    <>
    {!isDetailAssetView ? (
      <>
        <Helmet>
          <title>BookingDLite Asset Management</title>
        </Helmet>
        <PageTitleWrapper>
          <PageHeader
            breadcrumbs={breadcrumbs}
            heading={breadcrumbsHeading}
            subHeading={headerSubtitle}
          >
            {viewType == VIEW_TYPE.ASSET_MANAGEMENT && (
              <Button
                size={'small'}
                sx={{ pt: 1, pb: 1 }}
                className="letter-spacing"
                variant={'contained'}
                startIcon={<AddTwoToneIcon fontSize="small" />}
                onClick={handleAddUserButtonClick}
              >
                {t('car.add-asset')}
              </Button>
            )}
            <TextField
              size={'small'}
              id="outlined-search"
              label={t('car.search-assets')}
              type="search"
              onChange={(e) => handleFilterUsers(e.target.value)}
            />
            <ToggleButtonGroup
              value={selectedTab}
              exclusive
              onChange={handleViewOrientation}
              size={'small'}
            >
              <ToggleButton disableRipple value={LIST_VIEW.CARD_VIEW}>
                <GridViewSharpIcon />
              </ToggleButton>
              <ToggleButton disableRipple value={LIST_VIEW.TABLE_VIEW}>
                <TableRowsTwoToneIcon />
              </ToggleButton>
            </ToggleButtonGroup>
          </PageHeader>
        </PageTitleWrapper>

        <Container maxWidth="lg">
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="stretch"
            spacing={3}
          >
            <Grid item xs={12}>
              <AssetManagementView
                handleDeleteIconClick={handleDeleteIconClick}
                handleSwitchIconClick={handleSwitchIconClick}
                handleEditIconClick={handleEditIconClick}
                handleViewIconClick={handleViewIconClick}
                handleSellAssetReq={handleSellAssetReq}
                handleSellIconClick={handleSellIconClick}
                handleSellModalCancelClick={handleSellModalCancelClick}
                inputAssetData={assetList}
                inputLoginUserRole={loginUserRole}
                inputSelectedTab={selectedTab}
                viewType={viewType}
                userAssetsList={userAssetsList}
              />
            </Grid>
          </Grid>
          <AddAssetModal
            inputAssetTypeData={assetTypeList}
            isAddModal={isAddModal}
            open={open}
            onClose={handleClose}
            onSubmit={onSubmit}
            handleClose={handleClose}
            editObject={selectedAsset}
          ></AddAssetModal>
        </Container>
      </>) : <>
      <AssetDetails
        handlAssetViewBackClick={handlAssetViewBackClick}
        handleSellIconClick={handleSellIconClick}
        dataSelectedAsset={selectedAsset}
        inputBookingAssetData={bookingAssetList}
        inputAssetExpenceList={assetExpenceList}
        inputIsAssetSold={selectedOrganization?.isAssetSold}
      >
      </AssetDetails>
    </>
}
<CustomSellModal
            open={isSellModalOpen}
            selectedAsset={selectedAsset}
            handleSellAssetReq={handleSellAssetReq}
            onClose={handleSellModalCancelClick}
          /></>);
};

export default AssetManagement;
